const API_URL = import.meta.env.VITE_API_URL || '';

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
}

async function api<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body } = options;

  const response = await fetch(`${API_URL}/api/v1/admin${endpoint}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }

  return response.json();
}

// Sites API
export interface Site {
  id: string;
  domain: string;
  wp_username?: string;
  has_wp_password?: boolean;
  settings: SiteSettings;
  created_at: string;
}

export interface SiteSettings {
  primaryColor?: string;
  secondaryColor?: string;
  defaultWidgetType?: 'auto' | 'quiz' | 'poll';
  autoGenerate?: boolean;
  // Branding for share images
  brandName?: string;
  hasLogo?: boolean;
  ogBackgroundColor?: string;
  ogTextColor?: string;
  // Custom image generation prompts per game type (keyed by game type ID)
  imagePrompts?: Record<string, string>;
}

export interface CreateSiteResponse {
  site: {
    id: string;
    domain: string;
    api_key: string;
    webhook_url: string;
  };
}

export const sitesApi = {
  list: () => api<{ sites: Site[] }>('/sites'),

  get: (id: string) => api<{ site: Site }>(`/sites/${id}`),

  create: (data: { domain: string; wp_username?: string; wp_app_password?: string; settings?: SiteSettings }) =>
    api<CreateSiteResponse>('/sites', { method: 'POST', body: data }),

  update: (id: string, data: Partial<{ domain: string; wp_username: string; wp_app_password: string; settings: SiteSettings }>) =>
    api<{ site: Site }>(`/sites/${id}`, { method: 'PUT', body: data }),

  delete: (id: string) => api<{ success: boolean }>(`/sites/${id}`, { method: 'DELETE' }),

  getWidgets: (id: string) => api<{ widgets: Widget[] }>(`/sites/${id}/widgets`),

  // Logo management
  uploadLogo: async (id: string, file: File) => {
    const response = await fetch(`${API_URL}/api/v1/admin/sites/${id}/logo`, {
      method: 'POST',
      headers: {
        'Content-Type': file.type,
      },
      credentials: 'include',
      body: file,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || 'Upload failed');
    }
    return response.json() as Promise<{ success: boolean; message: string }>;
  },

  getLogoUrl: (id: string) => `${API_URL}/api/v1/admin/sites/${id}/logo`,

  deleteLogo: (id: string) => api<{ success: boolean }>(`/sites/${id}/logo`, { method: 'DELETE' }),
};

// Widgets API
export interface Widget {
  id: string;
  site_id: string;
  source_url: string;
  type: 'quiz' | 'poll';
  status: 'active' | 'draft' | 'archived';
  content: string;
  content_hash?: string;
  created_at: string;
  updated_at: string;
}

export interface ImageBucket {
  id: string;
  label: string;
}

export interface BackgroundsStatus {
  hasBackgrounds: boolean;
  buckets?: ImageBucket[];
}

export interface GenerateBackgroundsResponse {
  message: string;
  count?: number;
  buckets?: string[];
  exists?: boolean;
}

export interface WpSyncStatus {
  synced: boolean | null;
  wpWidgetId?: string | null;
  currentWidgetId?: string;
  reason?: string;
}

export const widgetsApi = {
  get: (id: string) => api<{ widget: Widget }>(`/widgets/${id}`),

  update: (id: string, data: { content?: string; status?: string }) =>
    api<{ widget: Widget }>(`/widgets/${id}`, { method: 'PUT', body: data }),

  delete: (id: string) =>
    api<{ success: boolean }>(`/widgets/${id}`, { method: 'DELETE' }),

  regenerate: (id: string, type?: string) =>
    api<{ widget: Widget }>(`/widgets/${id}/regenerate`, {
      method: 'POST',
      body: type ? { type } : undefined,
    }),

  // Check if widget is synced with WordPress post meta
  checkWpSync: (id: string) =>
    api<WpSyncStatus>(`/widgets/${id}/wp-sync`),

  // AI Background generation
  checkBackgrounds: (id: string) =>
    api<BackgroundsStatus>(`/widgets/${id}/backgrounds`),

  generateBackgrounds: (id: string) =>
    api<GenerateBackgroundsResponse>(`/widgets/${id}/generate-backgrounds`, { method: 'POST' }),
};

// Latest widgets (with site info)
export interface WidgetWithSite extends Widget {
  site_domain: string;
}

export const latestWidgetsApi = {
  list: (limit: number = 20) =>
    api<{ widgets: WidgetWithSite[] }>(`/widgets?limit=${limit}`),
};

// Analytics/Stats API
export interface AnalyticsDay {
  widget_id: string;
  date: string;
  views: number;
  starts: number;
  completions: number;
  shares: number;
}

export interface StatsSummary {
  total_views: number;
  total_starts: number;
  total_completions: number;
  total_shares: number;
  total_poll_votes: number;
  daily: AnalyticsDay[];
}

export interface WidgetStats extends StatsSummary {
  poll_results?: Array<{ option: string; count: number; percentage: number }>;
  total_votes?: number;
}

export const statsApi = {
  summary: (options?: { days?: number; siteId?: string }) => {
    const params = new URLSearchParams();
    if (options?.days) params.set('days', options.days.toString());
    if (options?.siteId) params.set('siteId', options.siteId);
    const qs = params.toString();
    return api<StatsSummary>(`/stats${qs ? `?${qs}` : ''}`);
  },

  widget: (widgetId: string, days?: number) => {
    const qs = days ? `?days=${days}` : '';
    return api<WidgetStats>(`/widgets/${widgetId}/stats${qs}`);
  },
};

// Image prompt defaults
export interface ImagePromptDefaults {
  prompts: Record<string, string>;
  placeholders: Record<string, Array<{ placeholder: string; description: string }>>;
}

export const imagePromptsApi = {
  getDefaults: () => api<ImagePromptDefaults>('/image-prompts/defaults'),
};
